import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/module'

import type { BrandGroupProviderModel } from '@domain-group/realhub-backend-api-types'
import type { ProviderModel } from '@models/types'

const watchEntityKeys = ['brandGroupProviders']

type BrandGroupFilters = {
  brand_group_id?: number,
  provider_id?: number,
  string?: string,
}

type Options = {
  entityKey?: string,
  filters?: BrandGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

interface UseBrandGroupProvidersPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredBrandGroupProviders: BrandGroupProviderModel[],
  filteredBrandGroupProvidersCount: number,
  hasFilteredBrandGroupProviders: boolean,
  loading: boolean,
}

function useBrandGroupProviders(options: Options = {}): UseBrandGroupProvidersPayload {
  const {
    entityKey, filters, requestOptions,
  } = options

  const {
    brand_group_id: filterBrandGroupId,
    provider_id: filterProviderId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { brandGroupProviders: brandGroupProvidersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const { brandGroupProviders, providers } = entities || {}

  const filteredBrandGroupProviders = useMemo((): BrandGroupProviderModel[] => {
    const filtered = Object.values(brandGroupProviders).filter((brandGroupProvider) => {
      const { brand_group_id, provider_id } = brandGroupProvider

      const provider: Partial<ProviderModel> = providers[provider_id] || {}
      const { title: providerTitle } = provider

      const matchBrandGroup: boolean = matchFilterNumber(brand_group_id, filterBrandGroupId)
      const matchProviderId: boolean = matchFilterNumber(provider_id, filterProviderId)
      const matchProviderTitle: boolean = matchFilterString(providerTitle, filterString)

      return matchBrandGroup && matchProviderId && matchProviderTitle
    })

    return sortArrayBy(
      filtered,
      'asc',
      ({ provider_id }) => providers[provider_id]?.title,
    )
  }, [brandGroupProvidersUpdatedAt, JSON.stringify(filters)])

  const filteredBrandGroupProvidersCount = filteredBrandGroupProviders.length
  const hasFilteredBrandGroupProviders = !!filteredBrandGroupProvidersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandGroupProvidersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandGroupProviders } = useReduxAction(
    'brandGroupProviders',
    'loadBrandGroupProviders',
    {
      entityKey,
      limit,
      ...requestOptions,
      ...filtersWithOffset,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandGroupProviders,
    filteredBrandGroupProvidersCount,
    hasFilteredBrandGroupProviders,
    loading: loadingBrandGroupProviders,
  }
}

export default useBrandGroupProviders
